import React, { ReactElement, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from "react-router-dom";
import { useQuery } from 'react-query';
import ErrorIcon from '@material-ui/icons/Error';
// import WarningIcon from '@material-ui/icons/Warning';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { green, orange } from '@material-ui/core/colors';

import {
  DataGrid,
  GridCellParams,
  GridSelectionModelChangeParams,
  GridColDef,
  GridPageChangeParams,
  GridSortModel,
  GridSortModelParams,
} from '@material-ui/data-grid';

import { useEnergyApi } from '../_api/EnergyApiContext';
import RecRegistryCertificateGeneration, { RecRegistryCertificateGenerationRequest, RecRegistryCertificateGenerationStatus } from '../_api/data/RecRegistryCertificateGeneration'
import { Paginated } from '../_api/data/pagination'


function statusColumn(params: GridCellParams): ReactElement {
  const status = params.value as RecRegistryCertificateGenerationStatus
  switch (status) {
    case RecRegistryCertificateGenerationStatus.ok:
      return <CheckCircleIcon style={{ color: green[500] }} />
    case RecRegistryCertificateGenerationStatus.processingRequired:
      return <CheckCircleIcon style={{ color: orange[500] }} />
    case RecRegistryCertificateGenerationStatus.error:
      return <ErrorIcon color="error" />
  }
}

const columns: GridColDef[] = [
  { field: 'registeredPersonNumber', headerName: 'RPN', width: 100 },
  { field: 'accreditationCodeId', headerName: 'Accreditation Code', flex: 1 },
  { field: 'generationYear', headerName: 'Generation Year', width: 200 },
  { field: 'status', headerName: 'Status', width: 120, renderCell: statusColumn },
];

type Props = {
  accreditationCodeId?: string
  generationYear?: number
  registeredPersonNumber?: number
  containsErrors?: boolean
}

const RecRegistryCertificateGenerationsDataTable: React.FC<Props> = ({ accreditationCodeId, generationYear, registeredPersonNumber, containsErrors }): React.ReactElement => {
  const defaultRequest = (): RecRegistryCertificateGenerationRequest => {
    const request = new RecRegistryCertificateGenerationRequest()
    request.accreditationCodeId = accreditationCodeId
    request.generationYear = generationYear
    request.registeredPersonNumber = registeredPersonNumber
    request.containsErrors = containsErrors
    return request
  }

  useEffect(() => {
    const newRequest = RecRegistryCertificateGenerationRequest.copy(request);
    newRequest.accreditationCodeId = accreditationCodeId
    newRequest.generationYear = generationYear
    newRequest.registeredPersonNumber = registeredPersonNumber
    newRequest.containsErrors = containsErrors
    setRequest(newRequest)
  }, [accreditationCodeId, generationYear, registeredPersonNumber, containsErrors]);

  const history = useHistory()
  const energyApi = useEnergyApi()
  const [request, setRequest] = React.useState(defaultRequest())

  const queryGenerations = (): Promise<Paginated<RecRegistryCertificateGeneration>> => {
    return energyApi.getRecRegistryCertificateGenerations(request)
  }

  const handlePageChange = (params: GridPageChangeParams) => {
    const newRequest = RecRegistryCertificateGenerationRequest.copy(request)
    newRequest.page = params.page
    newRequest.per = params.pageSize
    setRequest(newRequest);
  };

  const setSortModel = (model: GridSortModel) => {
    const newRequest = RecRegistryCertificateGenerationRequest.copy(request)
    newRequest.sort = model
    setRequest(newRequest)
  };

  const handleSortModelChange = (params: GridSortModelParams) => {
    if (params.sortModel !== request.sort) {
      setSortModel(params.sortModel);
    }
  };

  const handleSelectionChange = (params: GridSelectionModelChangeParams) => {
    const selection = params.selectionModel[0]
    if (selection) {
      console.log("Selection")
      console.log(selection)
      history.push(`/rec-registry/certificate-generations/${selection}`)
    }
  }

  // Queries
  const {
    isLoading,
    isError,
    error,
    data,
    isFetching,
    isPreviousData,
  } = useQuery<Paginated<RecRegistryCertificateGeneration>, Error>(['recRegistryCertificateGenerations', request], (queryGenerations), { keepPreviousData: true });

  if (isLoading) {
    return <span>Loading...</span>
  }

  if (isError) {
    return <span>Error...</span>
  }

  if (data) {
    return (
      <div style={{ width: '100%' }}>
        <DataGrid
          autoHeight={true}
          rows={data.items}
          columns={columns}
          pagination
          pageSize={request.per}
          onPageSizeChange={handlePageChange}
          page={data.metadata.page}
          rowCount={data.metadata.total}
          paginationMode="server"
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          onPageChange={handlePageChange}
          loading={isLoading}
          disableColumnFilter
          onSelectionModelChange={handleSelectionChange}
        />
      </div>
    )
  }

  return <span>Error</span>
};

RecRegistryCertificateGenerationsDataTable.propTypes = {
  accreditationCodeId: PropTypes.string,
  generationYear: PropTypes.number,
  registeredPersonNumber: PropTypes.number,
  containsErrors: PropTypes.bool,
}

export default RecRegistryCertificateGenerationsDataTable;
